import MockAdapter from "axios-mock-adapter"
import type { AuthUser } from "@/modules/auth/entities/AuthUser"
import { mockOrganizations } from "./mock-org-service"

const now = new Date().toISOString()

const createMockUser = (email: string): AuthUser => {
  return {
    user: {
      firstName: "John",
      lastName: "Doe",
      email,
      status: 1,
      createdAt: now,
      lastLoginAt: now,
    },
    // Must be real AppRole enum values — route guards call hasRole() against
    // these strings, so anything else (e.g. "admin") redirect-loops to blank.
    roles: [
      "OrganizationOwner",
      "RegisteredUser",
      "Admin",
      "SuperAdmin",
      "TeamMember",
    ],
    permissions: ["read", "write"],
    currentOrganizationId: mockOrganizations[0].id,
  }
}

const createMockEmployeeUser = (email: string): AuthUser => {
  return {
    user: {
      firstName: "New",
      lastName: "Employee",
      email,
      status: 1,
    },
    roles: ["Employee", "RegisteredUser"],
    permissions: ["read"],
    currentOrganizationId: mockOrganizations[0].id,
  }
}

export const registerAuthMocks = (mock: MockAdapter) => {
  mock.onPost("/auth/login").reply((config) => {
    const { email, password } = JSON.parse(config.data ?? "{}")

    if (typeof email !== "string" || email.length === 0) {
      return [400, { message: "Email is required" }]
    }

    if (typeof password !== "string" || password.length === 0) {
      return [400, { message: "Password is required" }]
    }

    if (email === "admin@example.com") {
      return [
        200,
        { isSuccess: true, value: createMockUser(email) }
      ]
    }

    return [
      200,
      { isSuccess: true, value: createMockEmployeeUser(email) },
    ]
  })

  mock.onGet("/auth/invite-info").reply((config) => {
    const { token, email } = config.params ?? {}

    if (!token || !email) {
      return [400, { isSuccess: false, error: "Missing token or email" }]
    }

    const isExistingUser =
      typeof email === "string" && email.includes("existing")

    return [
      200,
      {
        isSuccess: true,
        value: {
          isExistingUser,
          organizationName: "Acme Corp",
          firstName: isExistingUser ? "Jane" : "New Employee",
        },
      },
    ]
  })

  mock.onPost("/auth/complete-invite").reply((config) => {
    const { email, inviteToken, tempPassword, newPassword } = JSON.parse(
      config.data ?? "{}",
    )

    if (!email || !inviteToken || !tempPassword || !newPassword) {
      return [400, { isSuccess: false, error: "Missing required fields" }]
    }

    return [
      200,
      {
        isSuccess: true,
        value: {
          user: createMockEmployeeUser(email),
          roles: ["user"],
          permissions: ["read"],
          expiresAt: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
        },
      },
    ]
  })

  mock.onPost("/auth/resend-otp").reply((config) => {
    const { email } = JSON.parse(config.data ?? "{}")
    if (typeof email !== "string" || email.length === 0) {
      return [400, { message: "Email is required" }]
    }
    return [200, { success: true, message: "OTP sent successfully" }]
  })

  mock.onPut("/auth/validate-otp").reply((config) => {
    const { email, otp } = JSON.parse(config.data ?? "{}")
    if (!email || !otp) {
      return [400, { message: "Email and OTP are required" }]
    }
    return [200, { success: true }]
  })

  mock.onPost("/auth/signup").reply(() => {
    return [
      200,
      {
        success: true,
        message: "Signup successful",
      },
    ]
  })

  mock.onGet("/auth/profile").reply(200, createMockUser("admin@example.com"))

  mock.onPost("/auth/logout").reply(200)

  mock.onPost("/auth/refresh").reply(200, {
    user: createMockUser("admin@example.com"),
  })

  return mock
}
